// ReportTableOverviewMetricByMonth.jsx
import React, { useMemo } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
    Box,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import SmallPercentage from "../../SmallPercentage";

import Colours from "../../../helpers/colours";
import { fns } from "../../../helpers/common";
import {
    DISPLAY_ELEMENT_HELPER,
    getMetricName,
    GROUP_DATE_MONTH,
    GROUP_DATE_YEAR,
} from "../../../helpers/filters";
import {
    applyTargetsToEntries,
    getComparisonMetaUi,
} from "../../../helpers/targets";

dayjs.extend(utc);

const buildMonthKeys = (fromDay, toDay) => {
    const start = dayjs.utc(fromDay).startOf("month");
    const end = dayjs.utc(toDay).startOf("month");

    if (!start.isValid() || !end.isValid() || end.isBefore(start, "month")) {
        return [];
    }

    const out = [];
    let cursor = start;

    while (!cursor.isAfter(end, "month")) {
        out.push(cursor.format("YYYY-MM"));
        cursor = cursor.add(1, "month");
    }

    return out;
};

export const REPORT_TABLE_OVERVIEW_METRIC_BY_MONTH =
    "REPORT_TABLE_OVERVIEW_METRIC_BY_MONTH";

const ReportTableOverviewMetricByMonth = ({
    group,
    js,
    processFn,
    rawData,
    dataSimple,
    dataTargets,
    visibleMetrics,
    hotelID,
    place,
    metrics = DISPLAY_ELEMENT_HELPER,
    PDF = false,
    comparisonLabel,
}) => {
    const monthKeys = useMemo(
        () => buildMonthKeys(js.fromDay, js.toDay),
        [js.fromDay, js.toDay],
    );

    const monthlyJs = useMemo(
        () => ({
            ...js,
            groupBy: [GROUP_DATE_YEAR, GROUP_DATE_MONTH],
        }),
        [js],
    );

    const syntheticDataBookings = useMemo(
        () => ({ status: "loaded", data: rawData ?? [] }),
        [rawData],
    );

    const monthlyEntriesByMonth = useMemo(() => {
        if (!processFn) return {};

        const { entries } = processFn({
            js: monthlyJs,
            dataBookings: syntheticDataBookings,
            hotelID,
            dataSimple,
            dataTargets,
        });

        const entriesByMonth = Object.values(entries ?? {}).reduce(
            (acc, entry) => {
                const fromDateGroup =
                    entry?.result?.fromDateGroup ??
                    entry?.resultOneYearBefore?.fromDateGroup;
                const monthKey = fromDateGroup
                    ? dayjs.utc(fromDateGroup).format("YYYY-MM")
                    : "";
                if (monthKey && monthKey !== "Invalid Date") {
                    acc[monthKey] = entry;
                }
                return acc;
            },
            {},
        );

        const missingEntries = monthKeys.reduce((acc, monthKey) => {
            if (entriesByMonth[monthKey]) return acc;
            const monthStart = dayjs.utc(`${monthKey}-01`);
            acc[monthKey] = {
                result: {
                    fromDateGroup: monthStart.format("YYYY-MM-DD"),
                    toDateGroup: monthStart.endOf("month").format("YYYY-MM-DD"),
                },
                resultOneYearBefore: {},
                keys: [
                    [GROUP_DATE_YEAR, monthStart.format("YYYY")],
                    [GROUP_DATE_MONTH, monthStart.format("MM")],
                ],
                data: [],
            };
            return acc;
        }, {});

        const targetOnlyEntries = applyTargetsToEntries({
            entries: missingEntries,
            js: monthlyJs,
            dataSimple,
            dataTargets,
            baseComparisonType: monthlyJs?.filters?.Comparison,
            processFn: (targetJs) =>
                processFn({
                    js: targetJs,
                    dataBookings: syntheticDataBookings,
                    hotelID,
                    dataSimple,
                    dataTargets,
                }),
        });

        return { ...entriesByMonth, ...targetOnlyEntries };
    }, [
        processFn,
        monthlyJs,
        monthKeys,
        syntheticDataBookings,
        hotelID,
        dataSimple,
        dataTargets,
    ]);

    const months = useMemo(
        () =>
            monthKeys.map((monthKey) => ({
                key: monthKey,
                label: dayjs.utc(`${monthKey}-01`).format("MMM YYYY"),
                entry: monthlyEntriesByMonth[monthKey],
            })),
        [monthKeys, monthlyEntriesByMonth],
    );

    const headerStyles = {
        fontSize: "0.8em",
        backgroundColor: Colours.pillarBackgroundAttract,
        textAlign: "left",
        border: "1px solid lightgrey",
        fontWeight: "bold",
        whiteSpace: "nowrap",
    };

    // All column groups rendered in order: Total first, then each month
    const columnGroups = [
        { key: "total", label: "Total", isTotal: true },
        ...months,
    ];

    const getActual = (col, metric) =>
        col.isTotal ? group.result[metric] : col.entry?.result?.[metric];

    const getComp = (col, metric) =>
        col.isTotal
            ? (group.resultOneYearBefore ?? {})[metric]
            : col.entry?.resultOneYearBefore?.[metric];

    const getCompMeta = (col, metric) =>
        col.isTotal
            ? group.comparisonMetaByMetric?.[metric]
            : col.entry?.comparisonMetaByMetric?.[metric];

    const formatVal = (metric, value) =>
        value !== null && value !== undefined
            ? fns(hotelID, value, (metrics[metric] ?? {}).format ?? {})
            : "-";

    const renderDiff = (metric, actual, comp, hidePercentage) => {
        if (hidePercentage) return "-";

        if (!isFinite(Number(actual)) || !isFinite(Number(comp))) return "-";

        const hasComp =
            actual !== null &&
            actual !== undefined &&
            comp !== null &&
            comp !== undefined &&
            Number(comp) !== 0;

        if (!hasComp) return "-";

        const diff = Number(actual) - Number(comp);
        const fmt = (metrics[metric] ?? {}).format ?? {};
        const decimalFmt = {
            maxDecimals: fmt.maxDecimals,
            minDecimals: fmt.minDecimals,
        };

        return (
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={0.5}
            >
                <SmallPercentage
                    brackets={false}
                    tooltip={false}
                    bubble={true}
                    bubbleRound={true}
                    actual={actual}
                    old={comp}
                    showFormula={false}
                    isInverted={(metrics[metric] ?? {}).upside ?? false}
                />
                <Typography variant="caption">
                    {fns(hotelID, Math.abs(diff), decimalFmt)}
                </Typography>
            </Stack>
        );
    };

    const loaded =
        visibleMetrics.length > 0 && Object.keys(group.result ?? {}).length > 0;

    return (
        <Paper component={PDF ? "div" : undefined}>
            <TableContainer
                sx={{
                    overflow: "auto",
                    maxHeight: PDF ? "none" : "800px",
                }}
            >
                <Table stickyHeader size="medium">
                    <TableHead>
                        <TableRow>
                            <TableCell
                                style={{
                                    ...headerStyles,
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 4,
                                    minWidth: 200,
                                }}
                            >
                                Metric
                            </TableCell>

                            {columnGroups.map((col) => (
                                <TableCell
                                    key={col.key}
                                    colSpan={3}
                                    style={{
                                        ...headerStyles,
                                        textAlign: "center",
                                        fontStyle: col.isTotal
                                            ? "italic"
                                            : undefined,
                                    }}
                                >
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {/*  Sub-header info row: Actual | Comparison | Diff per column group  */}
                        <TableRow>
                            <TableCell
                                style={{
                                    border: "1px solid lightgrey",
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 1,
                                    backgroundColor: Colours.plainWhite,
                                }}
                            />
                            {columnGroups.map((col, i) => (
                                <React.Fragment key={col.key}>
                                    <TableCell
                                        style={{
                                            border: "1px solid lightgrey",
                                        }}
                                    >
                                        <Typography variant="caption">
                                            {i === 0 ? "Actual" : ""}
                                        </Typography>
                                    </TableCell>
                                    <TableCell
                                        style={{
                                            border: "1px solid lightgrey",
                                            backgroundColor:
                                                Colours.notificationCard,
                                        }}
                                    >
                                        <Typography variant="caption">
                                            {i === 0 ? "Comparison" : ""}
                                        </Typography>
                                    </TableCell>
                                    <TableCell
                                        style={{
                                            border: "1px solid lightgrey",
                                            backgroundColor:
                                                Colours.tableRowBackground,
                                        }}
                                    >
                                        <Typography variant="caption">
                                            {i === 0 ? "Diff" : ""}
                                        </Typography>
                                    </TableCell>
                                </React.Fragment>
                            ))}
                        </TableRow>

                        {/*  Data rows  */}
                        {loaded
                            ? visibleMetrics.map((metric) => (
                                  <TableRow key={metric}>
                                      <TableCell
                                          style={{
                                              fontWeight: "bold",
                                              border: "1px solid lightgrey",
                                              position: "sticky",
                                              left: 0,
                                              zIndex: 1,
                                              backgroundColor:
                                                  Colours.plainWhite,
                                          }}
                                      >
                                          {fns(
                                              hotelID,
                                              getMetricName(
                                                  metric,
                                                  place,
                                                  metrics,
                                              ) ?? metric,
                                              (metrics[metric] ?? {})
                                                  .headerFormat,
                                          )}
                                      </TableCell>

                                      {columnGroups.map((col) => {
                                          const actual = getActual(col, metric);
                                          const comp = getComp(col, metric);
                                          const isTarget =
                                              getComparisonMetaUi(
                                                  getCompMeta(col, metric),
                                              )?.isTarget ?? false;

                                          return (
                                              <React.Fragment key={col.key}>
                                                  <TableCell
                                                      sx={{
                                                          textAlign: "right",
                                                          border: "1px solid lightgrey",
                                                          fontWeight: "bold",
                                                      }}
                                                  >
                                                      <Stack
                                                          direction="row-reverse"
                                                          justifyContent="space-between"
                                                      >
                                                          {formatVal(
                                                              metric,
                                                              actual,
                                                          )}
                                                      </Stack>
                                                  </TableCell>
                                                  <TableCell
                                                      sx={{
                                                          textAlign: "right",
                                                          border: "1px solid lightgrey",
                                                          backgroundColor:
                                                              Colours.notificationCard,
                                                      }}
                                                  >
                                                      <Stack
                                                          direction="row"
                                                          justifyContent="flex-end"
                                                          alignItems="center"
                                                          spacing={0.5}
                                                      >
                                                          {isTarget && (
                                                              <Box
                                                                  sx={{
                                                                      px: 0.8,
                                                                      py: 0.15,
                                                                      borderRadius:
                                                                          "999px",
                                                                      fontSize:
                                                                          "0.68em",
                                                                      fontWeight:
                                                                          "bold",
                                                                      lineHeight: 1.4,
                                                                      color: Colours.primary,
                                                                      backgroundColor: `${Colours.primary}15`,
                                                                      border: `1px solid ${Colours.primary}30`,
                                                                      whiteSpace:
                                                                          "nowrap",
                                                                  }}
                                                              >
                                                                  T
                                                              </Box>
                                                          )}
                                                          {formatVal(
                                                              metric,
                                                              comp,
                                                          )}
                                                      </Stack>
                                                  </TableCell>
                                                  <TableCell
                                                      sx={{
                                                          ...(PDF && {
                                                              paddingLeft: 1,
                                                              paddingRight: 1,
                                                          }),
                                                          textAlign: "right",
                                                          border: "1px solid lightgrey",
                                                          backgroundColor:
                                                              Colours.tableRowBackground,
                                                          fontWeight:
                                                              col.isTotal
                                                                  ? "bold"
                                                                  : undefined,
                                                      }}
                                                  >
                                                      {renderDiff(
                                                          metric,
                                                          actual,
                                                          comp,
                                                          (
                                                              js.hidePercentageFor ??
                                                              []
                                                          ).includes(metric),
                                                      )}
                                                  </TableCell>
                                              </React.Fragment>
                                          );
                                      })}
                                  </TableRow>
                              ))
                            : [...Array(5).keys()].map((e) => (
                                  <TableRow key={e}>
                                      {[
                                          ...Array(
                                              1 + columnGroups.length * 3,
                                          ).keys(),
                                      ].map((i) => (
                                          <TableCell key={i}>
                                              <Skeleton />
                                          </TableCell>
                                      ))}
                                  </TableRow>
                              ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default ReportTableOverviewMetricByMonth;
